import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { clearPersistedDocumentRoute } from "@/documents/hooks/usePersistDocumentRoute";
import { cn } from "@/libs/utils";
import * as AccordionPrimitive from "@radix-ui/react-accordion";
import {
  ChevronDown,
  ChevronRight,
  FileIcon,
  FolderCog,
  FolderIcon,
  FolderOpenIcon,
  ScrollText,
  Settings
} from "lucide-react";
import React, {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";

type TreeViewElement = {
  id: string;
  name: string;
  isSelectable?: boolean;
  children?: TreeViewElement[];
};

type TreeContextProps = {
  selectedId: string | undefined;
  expendedItems: string[] | undefined;
  indicator: boolean;
  handleExpand: (id: string) => void;
  selectItem: (id: string) => void;
  setExpendedItems?: React.Dispatch<React.SetStateAction<string[] | undefined>>;
  openIcon?: React.ReactNode;
  closeIcon?: React.ReactNode;
  direction: "rtl" | "ltr";
};

const TreeContext = createContext<TreeContextProps | null>(null);

const useTree = () => {
  const context = useContext(TreeContext);
  if (!context) {
    throw new Error("useTree must be used within a TreeProvider");
  }
  return context;
};

interface TreeViewComponentProps extends React.HTMLAttributes<HTMLDivElement> { }

type Direction = "rtl" | "ltr" | undefined;

type TreeViewProps = {
  initialSelectedId?: string;
  indicator?: boolean;
  elements?: TreeViewElement[];
  initialExpendedItems?: string[];
  initialExpendedItemsKey?: string;
  openIcon?: React.ReactNode;
  closeIcon?: React.ReactNode;
} & TreeViewComponentProps;

/**
 * Tree View Docs: {@link: https://shadcn-extension.vercel.app/docs/tree-view}
 */

const Tree = forwardRef<HTMLDivElement, TreeViewProps>(
  (
    {
      className,
      elements,
      initialSelectedId,
      initialExpendedItems,
      initialExpendedItemsKey,
      children,
      indicator = true,
      openIcon,
      closeIcon,
      dir,
      ...props
    },
    ref
  ) => {
    const [selectedId, setSelectedId] = useState<string | undefined>(
      initialSelectedId
    );
    const [expendedItems, setExpendedItems] = useState<string[] | undefined>(
      initialExpendedItems
    );

    const selectItem = useCallback((id: string) => {
      setSelectedId(id);
    }, []);

    const handleExpand = useCallback((id: string) => {
      setExpendedItems((prev) => {
        if (prev?.includes(id)) {
          return prev.filter((item) => item !== id);
        }
        return [...(prev ?? []), id];
      });
    }, []);

    const expandSpecificTargetedElements = useCallback(
      (elements?: TreeViewElement[], selectId?: string) => {
        if (!elements || !selectId) return;
        const findParent = (
          currentElement: TreeViewElement,
          currentPath: string[] = []
        ) => {
          const isSelectable = currentElement.isSelectable ?? true;
          const newPath = [...currentPath, currentElement.id];
          if (currentElement.id === selectId) {
            if (isSelectable) {
              setExpendedItems((prev) => [...(prev ?? []), ...newPath]);
            } else {
              if (newPath.includes(currentElement.id)) {
                newPath.pop();
                setExpendedItems((prev) => [...(prev ?? []), ...newPath]);
              }
            }
            return;
          }
          if (
            isSelectable &&
            currentElement.children &&
            currentElement.children.length > 0
          ) {
            currentElement.children.forEach((child) => {
              findParent(child, newPath);
            });
          }
        };
        elements.forEach((element) => {
          findParent(element);
        });
      },
      []
    );

    useEffect(() => {
      if (initialSelectedId) {
        setSelectedId(initialSelectedId);
        expandSpecificTargetedElements(elements, initialSelectedId);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialSelectedId, elements]);

    useEffect(() => {
      if (initialExpendedItems) {
        setExpendedItems(initialExpendedItems);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialExpendedItemsKey]);

    const direction = dir === "rtl" ? "rtl" : "ltr";

    return (
      <TreeContext.Provider
        value={{
          selectedId,
          expendedItems,
          handleExpand,
          selectItem,
          setExpendedItems,
          indicator,
          openIcon,
          closeIcon,
          direction,
        }}
      >
        <div className={cn("", className)}>
          <ScrollArea
            ref={ref}
            className="relative h-full"
            dir={dir as Direction}
          >
            <AccordionPrimitive.Root
              {...props}
              type="multiple"
              defaultValue={expendedItems}
              value={expendedItems}
              className="flex flex-col gap-1"
              onValueChange={() => undefined}
              dir={dir as Direction}
            >
              {children}
            </AccordionPrimitive.Root>
          </ScrollArea>
        </div>
      </TreeContext.Provider>
    );
  }
);

Tree.displayName = "Tree";

const TreeIndicator = forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  const { direction } = useTree();

  return (
    <div
      dir={direction}
      ref={ref}
      className={cn(
        "h-full w-px bg-secondary/30 absolute left-1.5 rtl:right-1.5 py-3 rounded-md hover:bg-slate-300 duration-300 ease-in-out",
        className
      )}
      {...props}
    />
  );
});

TreeIndicator.displayName = "TreeIndicator";

interface FolderComponentProps
  extends React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Item> { }

type FolderProps = {
  expendedItems?: string[];
  element: string;
  isSelectable?: boolean;
  isSelect?: boolean;
  idElement?: number;
  visibleChevron?: boolean;
  icon?: string;
  url?: string;
} & FolderComponentProps;

const Folder = forwardRef<
  HTMLDivElement,
  FolderProps & React.HTMLAttributes<HTMLDivElement>
>(
  (
    {
      className,
      element,
      value,
      isSelectable = true,
      isSelect,
      children,
      idElement,
      visibleChevron = false,
      icon = "folder",
      url,
      ...props
    },
    ref
  ) => {
    const {
      direction,
      handleExpand,
      expendedItems,
      setExpendedItems,
      selectItem,
      openIcon,
      closeIcon,
    } = useTree();

    const navigate = useNavigate();
    const openFolder = (items: string[] | undefined, folderValue: string) =>
      items?.includes(folderValue)
        ? items
        : [...(items ?? []), folderValue];

    const handleSelectFolder = (event: React.MouseEvent<HTMLSpanElement>) => {
      event.stopPropagation();
      selectItem(value);
      setExpendedItems((prev) => {
        if (url) {
          navigate(url);
          return openFolder(prev, value);
        }

        if (value == "doc-parent") {
          clearPersistedDocumentRoute();
          navigate(`/dashboard/mes-documents`);
          return openFolder(prev, value);
        } else if (value.includes("doc-")) {
          const ids = children?.map((item) => item.key.replace("file-", ""));
          ids.push(idElement);
          const urlParams = "?categories=" + encodeURIComponent(ids.join(","));
          navigate(`/dashboard/mes-documents${children ? urlParams : ""}`);
          return openFolder(prev, value);
        } else if (value.includes("doc-child-")) {
          navigate(`/dashboard/mes-documents?categories=${idElement}`);
          return openFolder(prev, value);
        } else if (idElement !== undefined) {
          navigate(`/dashboard/project/${idElement}`);
          return openFolder(prev, value);
        } else {
          // Ne pas naviguer si ni url ni idElement ne sont définis
          return openFolder(prev, value);
        }
      });
    };
    return (
      <AccordionPrimitive.Item
        {...props}
        value={value}
        ref={ref}
        className="relative mt-0 overflow-hidden"
      >
        <AccordionPrimitive.Trigger
          className={cn(
            `flex items-center gap-1 text-sm rounded-md w-full `,
            className,
            {
              "bg-gray-200 rounded-md": isSelect && isSelectable,
              "cursor-pointer": isSelectable,
              "cursor-not-allowed opacity-50": !isSelectable,
            }
          )}
          disabled={!isSelectable}
          onClick={() => handleExpand(value)}
        >
          {visibleChevron &&
            (expendedItems?.includes(value)
              ? openIcon ?? (
                <ChevronDown
                  size={20}
                  className="mr-[-3px] text-muted-foreground opacity-45"
                />
              )
              : closeIcon ?? (
                <ChevronRight
                  size={20}
                  className="mr-[-3px] text-muted-foreground opacity-45"
                />
              ))}
          {value === "doc-parent" ? (
            <ScrollText className="size-5 text-muted-foreground " />
          ) : expendedItems?.includes(value) ? (
            openIcon ?? (
              <FolderOpenIcon className="size-5 text-muted-foreground " />
            )
          ) : (
            closeIcon ?? (
              <FolderIcon className="size-5 text-muted-foreground " />
            )
          )}
          <span
            title={element}
            className={`line-clamp-1 flex-1 text-left text-[13px] leading-4 text-muted-foreground hover:text-[#1C5114] hover:opacity-100`}
            style={{ maxWidth: "calc(100% - 35px)" }}
            onClick={handleSelectFolder}
          >
            {element}
          </span>
        </AccordionPrimitive.Trigger>
        <AccordionPrimitive.Content className="relative mt-2 overflow-hidden text-sm data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
          {/* {element && indicator && <TreeIndicator aria-hidden="true" />} */}
          <AccordionPrimitive.Root
            dir={direction}
            type="multiple"
            className="folder-arrow ml-5 flex flex-col gap-1 py-1 rtl:mr-5"
            defaultValue={expendedItems}
            value={expendedItems}
            onValueChange={() => undefined}
          >
            {children}
          </AccordionPrimitive.Root>
        </AccordionPrimitive.Content>
      </AccordionPrimitive.Item>
    );
  }
);

Folder.displayName = "Folder";

const File = forwardRef<
  HTMLButtonElement,
  {
    value: string;
    handleSelect?: (id: string) => void;
    isSelectable?: boolean;
    isSelect?: boolean;
    url: string;
    fileIcon?: React.ReactNode;
  } & React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger>
>(
  (
    {
      value,
      className,
      isSelectable = true,
      isSelect,
      fileIcon,
      children,
      url,
      ...props
    },
    ref
  ) => {
    const { direction, selectedId, selectItem } = useTree();
    const isSelected = isSelect ?? selectedId === value;
    const navigate = useNavigate();
    return (
      <AccordionPrimitive.Item value={value} className="relative">
        <AccordionPrimitive.Trigger
          ref={ref}
          {...props}
          dir={direction}
          disabled={!isSelectable}
          aria-label="File"
          className={cn(
            "flex items-center gap-1 cursor-pointer text-sm pr-1 rtl:pl-1 rtl:pr-0 rounded-md  duration-200 ease-in-out",
            {
              "": isSelected && isSelectable,
            },
            isSelectable ? "cursor-pointer" : "cursor-not-allowed",
            isSelected ? "bg-gray-200 p-1" : "",
            className
          )}
          onClick={() => {
            selectItem(value);
            navigate(url);
          }}
        >
          {fileIcon ?? <FileIcon className="size-6" />}
          {children}
        </AccordionPrimitive.Trigger>
      </AccordionPrimitive.Item>
    );
  }
);

File.displayName = "File";

const CollapseButton = forwardRef<
  HTMLButtonElement,
  {
    elements: TreeViewElement[];
    expandAll?: boolean;
  } & React.HTMLAttributes<HTMLButtonElement>
>(({ elements, expandAll = false, children, ...props }, ref) => {
  const { expendedItems, setExpendedItems } = useTree();

  const expendAllTree = useCallback((elements: TreeViewElement[]) => {
    const expandTree = (element: TreeViewElement) => {
      const isSelectable = element.isSelectable ?? true;
      if (isSelectable && element.children && element.children.length > 0) {
        setExpendedItems?.((prev) => [...(prev ?? []), element.id]);
        element.children.forEach(expandTree);
      }
    };

    elements.forEach(expandTree);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const closeAll = useCallback(() => {
    setExpendedItems?.([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (expandAll) {
      expendAllTree(elements);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandAll]);

  return (
    <Button
      variant={"ghost"}
      className="absolute bottom-1 right-2 h-8 w-fit p-1"
      onClick={
        expendedItems && expendedItems.length > 0
          ? closeAll
          : () => expendAllTree(elements)
      }
      ref={ref}
      {...props}
    >
      {children}
      <span className="sr-only">Toggle</span>
    </Button>
  );
});

CollapseButton.displayName = "CollapseButton";

export { CollapseButton, File, Folder, Tree, type TreeViewElement };
